/* eslint-disable no-shadow */
import {
  InputAdornment,
  MenuItem,
  OutlinedInput,
  OutlinedInputProps,
  Select,
  SelectProps,
  styled,
} from '@material-ui/core';
import { getPhoneNumberInfo } from '@src/utils/formatPhoneNumber';
import dialCodes from '@utils/dialCodes.json';
import { useController } from 'react-hook-form';
import { useFieldControllerContext } from './FieldControllerContext';
import { fieldHasError } from './formUtils';

const DialCodeSelect = styled(Select)({
  marginLeft: -8,
  marginRight: -8,
  '& fieldset': {
    border: 'none',
    paddingRight: 0,
  },
  '& .MuiSelect-root': {
    paddingRight: 20,
    borderTopLeftRadius: 8,
    borderBottomLeftRadius: 8,
  },
  '& .MuiSvgIcon-root': {
    marginRight: -8,
  },
});

const CountryMenuItem = styled(MenuItem)({
  display: 'flex',
  alignItems: 'center',
  '& .country': {
    order: 2,
  },
  '& .flag': {
    order: 1,
    marginRight: 8,
    width: '1.5rem',
  },
});

const NOT_PHONE_DIGIT_REGEX = /[^+\d ]/g;

const FieldPhoneNumberInput: React.FC<OutlinedInputProps> = ({
  disabled,
  ...props
}) => {
  const { name, inputLabel } = useFieldControllerContext();

  const {
    fieldState,
    field: { onBlur, onChange, ref, value },
  } = useController({ name });

  const { dialCode, phoneNumber, flag } = getPhoneNumberInfo(
    value?.replace(NOT_PHONE_DIGIT_REGEX, '') || '',
    'CO'
  );

  const onPhoneNumberChange: React.ChangeEventHandler<
    HTMLInputElement
  > = ev => {
    const nextPhoneNumber = ev.target.value
      .replace(NOT_PHONE_DIGIT_REGEX, '')
      .trim();

    if (nextPhoneNumber !== phoneNumber) {
      onChange(`${dialCode} ${nextPhoneNumber}`);
    }
  };

  const onDialCodeChange: SelectProps['onChange'] = ev => {
    const nextDialCode = ev.target.value;

    if (nextDialCode !== dialCode) {
      onChange(`${nextDialCode} ${phoneNumber}`);
    }
  };

  return (
    <OutlinedInput
      inputRef={ref}
      id={name}
      name={name}
      label={inputLabel}
      onBlur={onBlur}
      onChange={onPhoneNumberChange}
      onFocus={ev => {
        ev.target.select();
      }}
      value={phoneNumber}
      error={fieldHasError(fieldState)}
      disabled={disabled}
      aria-describedby={`${name}-helper-text`}
      startAdornment={
        <InputAdornment position="start">
          <DialCodeSelect
            value={dialCode}
            onChange={onDialCodeChange}
            disabled={disabled}
            renderValue={itemValue => (
              <span>
                {flag} {itemValue as React.ReactNode}
              </span>
            )}
          >
            {dialCodes.map(item => (
              <CountryMenuItem
                key={item.code}
                value={item.dialCode}
                dense
                style={{ display: 'flex' }}
              >
                <span className="country">{item.name}</span>
                <span className="flag">{item.flag}</span>
              </CountryMenuItem>
            ))}
          </DialCodeSelect>
        </InputAdornment>
      }
      {...props}
    />
  );
};

export default FieldPhoneNumberInput;
